import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const InstallCtx = createContext({ canInstall: false, promptInstall: () => Promise.resolve(false), installed: false });

const DISMISS_KEY = "dkc-install-dismissed-at";
const DISMISS_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export function PWAInstallProvider({ children }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true;
    if (isStandalone) setInstalled(true);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return false;
    try {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      setDeferred(null);
      return outcome === "accepted";
    } catch {
      return false;
    }
  }, [deferred]);

  const value = useMemo(() => ({
    canInstall: !!deferred && !installed,
    installed,
    promptInstall,
    markDismissed: () => localStorage.setItem(DISMISS_KEY, String(Date.now())),
    isRecentlyDismissed: () => {
      const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
      return t && Date.now() - t < DISMISS_MS;
    },
  }), [deferred, installed, promptInstall]);

  return (
    <InstallCtx.Provider value={value}>
      {children}
    </InstallCtx.Provider>
  );
}

export function usePWAInstall() {
  return useContext(InstallCtx);
}
